import type { DraftCellsWire } from "@mk/shared";
import type { DraftCells } from "../boardDraft.ts";

export interface DraftRelaySnapshot {
  enabled: boolean;
  matchId: string;
  row: number;
  cells: DraftCellsWire;
}

export function toDraftCellsWire(cells: DraftCells): DraftCellsWire {
  return cells.map((cell) => cell ?? null) as DraftCellsWire;
}

function snapshotKey(snapshot: DraftRelaySnapshot): string {
  return `${snapshot.matchId}:${snapshot.row}:${snapshot.cells.map((cell) => cell ?? "_").join("")}`;
}

function identityOf(snapshot: DraftRelaySnapshot): string {
  return `${snapshot.matchId}:${snapshot.row}`;
}

function hasLetter(cells: DraftCellsWire): boolean {
  return cells.some((cell) => cell !== null);
}

/**
 * 200 ms leading + trailing yayın denetleyicisi.
 *
 * React'ten bağımsız tutulduğu için timer davranışı sahte saatle doğrudan
 * sınanabilir. `enabled` sıfıra düşünce bütün geçmiş sıfırlanır; yeniden
 * açıldığında yalnızca harf içeren durum hemen yayınlanır.
 */
export function createDraftRelay(
  send: (payload: Omit<DraftRelaySnapshot, "enabled">) => void,
  intervalMs = 200,
) {
  let enabled = false;
  let identity: string | null = null;
  let observedKey: string | null = null;
  let sentKey: string | null = null;
  let lastSentAt = Number.NEGATIVE_INFINITY;
  let pending: DraftRelaySnapshot | null = null;
  let timer: ReturnType<typeof setTimeout> | null = null;

  const cancelTimer = () => {
    if (timer !== null) clearTimeout(timer);
    timer = null;
    pending = null;
  };

  const sendNow = (snapshot: DraftRelaySnapshot) => {
    cancelTimer();
    const { enabled: _enabled, ...payload } = snapshot;
    send(payload);
    sentKey = snapshotKey(snapshot);
    lastSentAt = Date.now();
  };

  const schedule = (snapshot: DraftRelaySnapshot) => {
    pending = snapshot;
    if (timer !== null) return;
    const wait = Math.max(0, intervalMs - (Date.now() - lastSentAt));
    timer = setTimeout(() => {
      timer = null;
      const next = pending;
      pending = null;
      if (next && enabled && identityOf(next) === identity) sendNow(next);
    }, wait);
  };

  return {
    update(snapshot: DraftRelaySnapshot) {
      if (!snapshot.enabled) {
        enabled = false;
        identity = null;
        observedKey = null;
        sentKey = null;
        lastSentAt = Number.NEGATIVE_INFINITY;
        cancelTimer();
        return;
      }

      const nextIdentity = identityOf(snapshot);
      const key = snapshotKey(snapshot);
      if (!enabled || identity !== nextIdentity) {
        cancelTimer();
        enabled = true;
        identity = nextIdentity;
        observedKey = key;
        sentKey = null;
        lastSentAt = Number.NEGATIVE_INFINITY;
        // İlk harf izleyicisi geldiğinde dolu yerel taslak hemen görünür;
        // boş pasif cihazlar ise başka cihazın taslağını ezmez.
        if (hasLetter(snapshot.cells)) sendNow(snapshot);
        return;
      }

      if (key === observedKey) return;
      observedKey = key;

      // Kullanıcı bekleyen değişiklikten vazgeçip sunucunun zaten bildiği
      // duruma döndüyse trailing paketin anlamı kalmadı.
      if (key === sentKey) {
        cancelTimer();
        return;
      }

      if (Date.now() - lastSentAt >= intervalMs) sendNow(snapshot);
      else schedule(snapshot);
    },

    stop() {
      enabled = false;
      identity = null;
      observedKey = null;
      sentKey = null;
      lastSentAt = Number.NEGATIVE_INFINITY;
      cancelTimer();
    },
  };
}
